import {
  PERMISSION_ACTION_COMPLETE,
  PERMISSION_ACTION_DELETE,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_PUBLISH,
  ROUTE_PAYABLE
} from '@klear/constants'
import { PAYABLE_ACTION_MARK_PAID } from '@klear/payable/constants'
import type { Payable } from '@klear/payable/payable'
import {
  PAYABLE_ACTION_LABELS,
  PAYABLE_ROW_ACTIONS,
  PAY_NOW_SENDING_TOAST,
  payableAvailableActions,
  type PayableLifecycleAction
} from '@klear/payable/list-display'
import { usePayableCompletePreview } from '@klear/order-event-ui/use-payable-complete-preview'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { useOrderEventInvalidate } from '@klear/ui/hooks/use-order-event-invalidate'
import { trpc } from '@klear/ui/lib/trpc'
import { DataTableRowActions } from '@klear/ui/components/data-table-row-actions'
import { DropdownMenuItem } from '@klear/ui/components/dropdown-menu'
import { Modal } from '@klear/ui/components/modal'
import { PaymentPreview } from '@klear/ui/components/payment-preview'
import { toast } from '@klear/ui/hooks/use-toast'
import { PayableMarkPaidModal, type PayableMarkPaidFormValues } from '@/app/payables/payable-mark-paid-modal'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import isError from 'lodash/isError'
import toString from 'lodash/toString'

const ACTION_MODAL_COPY: Partial<
  Record<PayableLifecycleAction, { title: string; description: string; primaryText: string }>
> = {
  [PERMISSION_ACTION_PUBLISH]: {
    title: 'Schedule Payment?',
    description: 'The payment will be sent on the scheduled date unless you complete it early.',
    primaryText: 'Schedule Payment'
  },
  [PERMISSION_ACTION_COMPLETE]: {
    title: 'Send Payment Now?',
    description: 'Review the payment before sending. This action cannot be undone.',
    primaryText: 'Send Now'
  },
  [PERMISSION_ACTION_DELETE]: {
    title: 'Delete Payment?',
    description: 'This will remove the payment draft or cancel a scheduled payment that has not been sent.',
    primaryText: 'Delete'
  }
}

export function PayableActions({
  payable,
  traderId,
  onActionSuccess,
  allowedActions = PAYABLE_ROW_ACTIONS
}: {
  payable: Payable
  traderId: number
  onActionSuccess: () => void
  allowedActions?: readonly PayableLifecycleAction[]
}) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { urlPrefixApp } = useTrader(toString(traderId))
  const invalidateOrderEvents = useOrderEventInvalidate()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [markPaidOpen, setMarkPaidOpen] = useState(false)
  const [selectedAction, setSelectedAction] = useState<PayableLifecycleAction | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const publishPayment = trpc.payable.publishPayment.useMutation()
  const completePayment = trpc.payable.completePayment.useMutation()
  const markPaid = trpc.payable.markPaid.useMutation()
  const deletePayable = trpc.payable.delete.useMutation()

  const isCompleteAction = selectedAction === PERMISSION_ACTION_COMPLETE
  const {
    payment: paymentPreview,
    sourceAccount,
    destinationAccount,
    isLoading: isPreviewLoading
  } = usePayableCompletePreview(payable, traderId, dialogOpen && isCompleteAction)
  const isPreviewUnresolved = isPreviewLoading || !paymentPreview || !sourceAccount || !destinationAccount

  const availableActions = useMemo(
    () => payableAvailableActions(payable, user, allowedActions),
    [allowedActions, payable, user]
  )

  const modalCopy = selectedAction ? ACTION_MODAL_COPY[selectedAction] : null

  const handleAction = (action: PayableLifecycleAction) => {
    if (action === PERMISSION_ACTION_EDIT) {
      navigate(`${urlPrefixApp}/${ROUTE_PAYABLE}/${payable.id}/edit`)
      return
    }
    if (action === PAYABLE_ACTION_MARK_PAID) {
      setMarkPaidOpen(true)
      return
    }
    setSelectedAction(action)
    setDialogOpen(true)
  }

  const finishSuccess = async (title: string, description = 'The payable list has been updated.') => {
    await invalidateOrderEvents()
    onActionSuccess()
    toast({ title, description })
  }

  const handleConfirm = async () => {
    if (selectedAction == null || payable.id == null) return
    setIsLoading(true)
    try {
      const input = { id: payable.id, orderId: payable.orderId, traderId }
      if (selectedAction === PERMISSION_ACTION_PUBLISH) {
        await publishPayment.mutateAsync(input)
      } else if (selectedAction === PERMISSION_ACTION_COMPLETE) {
        await completePayment.mutateAsync(input)
      } else if (selectedAction === PERMISSION_ACTION_DELETE) {
        await deletePayable.mutateAsync(input)
      }
      setDialogOpen(false)
      setSelectedAction(null)
      if (selectedAction === PERMISSION_ACTION_COMPLETE) {
        await finishSuccess(PAY_NOW_SENDING_TOAST.title, PAY_NOW_SENDING_TOAST.description)
      } else {
        await finishSuccess(`${PAYABLE_ACTION_LABELS[selectedAction]} successful`)
      }
    } catch (error: unknown) {
      toast({
        title: 'Action failed',
        description: isError(error) ? error.message : 'An error occurred while performing the action.',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleMarkPaidConfirm = async (values: PayableMarkPaidFormValues) => {
    if (payable.id == null) return
    setIsLoading(true)
    try {
      await markPaid.mutateAsync({
        id: payable.id,
        orderId: payable.orderId,
        traderId,
        provider: 'manual',
        paymentDate: values.paymentDate,
        externalReference: values.externalReference,
        notes: values.notes
      })
      setMarkPaidOpen(false)
      await finishSuccess('Marked as paid')
    } catch (error: unknown) {
      toast({
        title: 'Action failed',
        description: isError(error) ? error.message : 'An error occurred while performing the action.',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <DataTableRowActions>
        {payable.id != null ? (
          <DropdownMenuItem onClick={() => navigate(`${urlPrefixApp}/${ROUTE_PAYABLE}/${payable.id}`)}>
            View
          </DropdownMenuItem>
        ) : null}
        {payable.id == null && availableActions.length === 0 ? (
          <DropdownMenuItem disabled>No actions available</DropdownMenuItem>
        ) : (
          availableActions.map((actionOption) => (
            <DropdownMenuItem key={actionOption.value} onClick={() => handleAction(actionOption.value)}>
              {actionOption.label}
            </DropdownMenuItem>
          ))
        )}
      </DataTableRowActions>

      {dialogOpen && modalCopy && selectedAction != null ? (
        <Modal
          size={isCompleteAction ? 'lg' : 'sm'}
          open={dialogOpen}
          onOpenChange={(open) => {
            if (!open && !isLoading) {
              setDialogOpen(false)
              setSelectedAction(null)
            }
          }}
          title={modalCopy.title}
          description={modalCopy.description}
          primaryText={isLoading ? 'Processing...' : modalCopy.primaryText}
          primaryDisabled={isLoading || (isCompleteAction && isPreviewUnresolved)}
          secondaryText="Cancel"
          secondaryDisabled={isLoading}
          handleSecondaryAction={() => {
            setDialogOpen(false)
            setSelectedAction(null)
          }}
          handlePrimaryAction={() => void handleConfirm()}
        >
          {isCompleteAction ? (
            isPreviewLoading ? (
              <div className="text-sm text-muted-foreground">Loading payment preview...</div>
            ) : paymentPreview && sourceAccount && destinationAccount ? (
              <PaymentPreview
                payment={paymentPreview}
                sourceAccount={sourceAccount}
                destinationAccount={destinationAccount}
              />
            ) : (
              <div className="text-sm text-muted-foreground">Payment preview unavailable.</div>
            )
          ) : null}
        </Modal>
      ) : null}

      <PayableMarkPaidModal
        open={markPaidOpen}
        isLoading={isLoading}
        onClose={() => setMarkPaidOpen(false)}
        onConfirm={handleMarkPaidConfirm}
      />
    </>
  )
}
